/**
 * Header-based session ID fallback for cross-site deployments (e.g. shop.fitracking.com
 * talking to api.tryfi.com). Chrome blocks third-party cookies in this scenario, so the
 * fi.sid session cookie never reaches the API. The backend already supports an
 * x-session-id header as an alternative — this module persists the session ID returned
 * in auth responses to localStorage and attaches it to subsequent requests.
 *
 * Using localStorage rather than an in-memory variable ensures the session ID survives
 * page refreshes. On first load we also check for a fi_session_id cookie (e.g. injected
 * by the native app's webview) and seed localStorage from it so the very first API call
 * can authenticate.
 */

import Cookies from 'js-cookie';

import { getCookieDomain } from '../util/cookieDomain';
import { DEV_MODE } from '../util/devMode';

const STORAGE_KEY = 'fi_session_id';

// The mobile Apps inject a fi_session_id cookie for both .fitracking.com and .tryfi.com.
// However now that we are serving this site at shop.fitracking.com neither cookie gets
// forwarded automatically to api.tryfi.com: the .fitracking cookie doesn't match the domain
// and from the perspective of this site the .tryfi cookie is a third-party cookie which
// also doesn't get forwarded. So if provided, store the sessionId now in local storage,
// so it will be attached by the request interceptor.
if (!getSessionId()) {
  const cookieSessionId = Cookies.get(STORAGE_KEY);
  if (cookieSessionId) {
    setSessionId(cookieSessionId);
  }
}

export function getSessionId() {
  const stored = typeof window === 'undefined' ? null : window.localStorage.getItem(STORAGE_KEY);
  if (stored) return stored;

  // retry cookie read — mobile app webviews inject the fi_session_id cookie
  // asynchronously (WKWebView's httpCookieStore.setCookie is async), so the
  // cookie may not have been available when this module first loaded.
  const cookieSessionId = Cookies.get(STORAGE_KEY);
  if (cookieSessionId) {
    setSessionId(cookieSessionId);
    return cookieSessionId;
  }

  return null;
}

export function setSessionId(id: string) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, id);
  }

  // Also write to a parent-domain cookie so sibling sites (e.g. marketingsite on fitracking.com)
  // can read the session ID and authenticate against api.tryfi.com, where the fi.sid cookie is
  // blocked as a third-party cookie. This mirrors what mobile apps do with fi_session_id cookies.
  Cookies.set(STORAGE_KEY, id, {
    expires: 30,
    domain: getCookieDomain(),
    secure: !DEV_MODE,
    sameSite: 'lax',
    path: '/',
  });
}

export function clearSessionId() {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(STORAGE_KEY);
  }
  Cookies.remove(STORAGE_KEY, { domain: getCookieDomain(), path: '/' });
}
